import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useAuth } from "../auth";
import type { Brand } from "../theme/brand";
import { Button, Card, Field, Input, LanguageSelector, ThemeToggle } from "../ui";

const schema = z.object({
  username: z.string().min(1),
  password: z.string().min(1),
});

type LoginForm = z.infer<typeof schema>;

/** Shared across all three apps; only the brand differs. */
export function LoginView({ brand }: { brand: Brand }) {
  const { login } = useAuth();
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async ({ username, password }) => {
    setFailed(false);
    try {
      await login(username, password);
    } catch {
      // The backend answers a bad login with a bare 401 and no body, so there
      // is nothing more specific to show than "those credentials didn't work".
      setFailed(true);
    }
  });

  return (
    <div className="flex min-h-full items-center justify-center bg-ground p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {brand.logo && <img src={brand.logo} alt="" className="size-6" />}
            <span className="text-lg font-semibold tracking-tight text-ink">{brand.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <ThemeToggle />
          </div>
        </div>

        <Card className="p-6">
          <h1 className="text-base font-semibold text-ink">{t("login.title", "Sign in")}</h1>
          <p className="mt-1 text-[13px] text-ink-muted">
            {t("login.description", "Use your account credentials to continue.")}
          </p>

          <form onSubmit={onSubmit} className="mt-5 flex flex-col gap-4">
            <Field
              label={t("form.field.username", "Username")}
              htmlFor="username"
              required
              error={errors.username && t("form.validation.field.required", "Required")}
            >
              <Input
                id="username"
                autoComplete="username"
                autoFocus
                invalid={Boolean(errors.username)}
                {...register("username")}
              />
            </Field>

            <Field
              label={t("form.field.password", "Password")}
              htmlFor="password"
              required
              error={errors.password && t("form.validation.field.required", "Required")}
            >
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                invalid={Boolean(errors.password)}
                {...register("password")}
              />
            </Field>

            {failed && (
              <p role="alert" className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
                {t("login.failed", "That username and password didn't match. Try again.")}
              </p>
            )}

            <Button type="submit" variant="primary" loading={isSubmitting} className="mt-1 w-full">
              {t("button.login", "Sign in")}
            </Button>
          </form>
        </Card>

        <p className="mt-4 text-center text-[12px] text-ink-faint">
          {t("login.copyright", "Copyright {{year}} Discreet Logic Inc.", {
            year: new Date().getFullYear(),
          })}
        </p>
      </div>
    </div>
  );
}
